import React, { useRef, useState, useEffect } from "react";
import { useSettings } from "../context/SettingsContext";

function useInView(threshold = 0.15) {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(
      ([e]) => {
        if (e.isIntersecting) {
          setInView(true);
          obs.disconnect();
        }
      },
      { threshold },
    );
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, [threshold]);
  return [ref, inView];
}

const services = [
  {
    num: "01",
    title: "Restaurant & Cafe",
    desc: "Helping entrepreneurs identify the right franchise opportunities based on their budget, interests, and business objectives.",
    img: "https://images.unsplash.com/photo-1517248135467-4c7edcad34c4?w=500&q=80",
  },
  {
    num: "02",
    title: "Brand Expansion",
    desc: "Supporting businesses in expanding their reach through structured franchise development and growth strategies.",
    img: "https://images.unsplash.com/photo-1552566626-52f8b828add9?w=500&q=80",
  },
  {
    num: "03",
    title: "Location Assistance",
    desc: "Helping clients identify suitable locations that align with their business model and target audience.",
    img: "https://images.unsplash.com/photo-1559339352-11d035aa65de?w=500&q=80",
  },
  {
    num: "04",
    title: "Business Guidance",
    desc: "Providing end-to-end support: from opportunity selection to franchise onboarding and business setup.",
    img: "https://images.unsplash.com/photo-1514190051997-0f6f39ca5cde?w=500&q=80",
  },
];

function Hero() {
  const { getBanner } = useSettings();
  const bannerSrc = getBanner("about");

  return (
    <div className="relative h-52 sm:h-64 md:h-80 overflow-hidden">
      <img
        src={bannerSrc || "images/bg1.png"}
        alt="About Us"
        className="absolute inset-0 w-full h-full object-cover object-center"
      />
      <div className="absolute inset-0 bg-black/55" />
      <div className="relative z-10 h-full flex items-center justify-center">
        <h1
          className="font-display text-white font-light"
          style={{ fontSize: "clamp(2.2rem, 5.5vw, 4rem)" }}
        >
          About Us
        </h1>
      </div>
    </div>
  );
}

function IntroCard() {
  const [ref, inView] = useInView();
  return (
    <div className="px-4 sm:px-8 md:px-12 lg:px-20 -mt-10 md:-mt-14 relative z-10 mb-16 md:mb-24">
      <div
        ref={ref}
        className={`max-w-2xl mx-auto bg-[#F4EEE6] rounded-2xl shadow-lg px-8 py-10 md:px-12 md:py-12 text-center transition-all duration-700 ${inView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}
      >
        <p className="font-body text-stone-600 text-sm md:text-base leading-relaxed mb-5">
          We are a team of business consultants, franchise experts, and growth
          strategists who understand the challenges of starting and scaling a
          business. Our goal is to simplify the franchise process by providing
          the right guidance, market insights, and opportunities to our clients.
        </p>
        <div className="w-12 h-px bg-gold mx-auto mb-5" />
        <p className="font-body text-stone-500 text-sm md:text-base leading-relaxed">
          Whether you're an investor looking for a profitable venture or a brand
          planning expansion, we offer practical solutions tailored to your
          goals.
        </p>
      </div>
    </div>
  );
}

function WhoWeAre() {
  const [ref, inView] = useInView();
  return (
    <section
      ref={ref}
      className="max-w-7xl mx-auto px-4 sm:px-8 md:px-12 lg:px-20 mb-20 md:mb-28"
    >
      <div className="grid grid-cols-1 md:grid-cols-2 gap-10 md:gap-16 items-center">
        <div
          className={`transition-all duration-700 ${inView ? "opacity-100 translate-x-0" : "opacity-0 -translate-x-8"}`}
        >
          <div className="relative">
            <div className="absolute -bottom-3 -left-3 w-full h-full border border-gold/30 rounded-br-[40px]" />
            <img
              src="images/bg4.png"
              alt="Who We Are"
              className="relative z-10 w-full h-64 sm:h-80 md:h-96 object-cover rounded-br-[40px]"
            />
          </div>
        </div>
        <div
          className={`transition-all duration-700 delay-150 ${inView ? "opacity-100 translate-x-0" : "opacity-0 translate-x-8"}`}
        >
          <h2
            className="font-display font-normal text-stone-900 leading-tight mb-5"
            style={{ fontSize: "clamp(2rem, 4vw, 3.2rem)" }}
          >
            Who We Are?
          </h2>
          <p className="font-body text-stone-500 text-sm md:text-base leading-relaxed mb-5">
            Franchise Business Solutions is dedicated to helping entrepreneurs,
            investors, and hospitality brands unlock new growth opportunities.
            We specialize in providing strategic business solutions for cafés,
            restaurants, bars, clubs, and other hospitality ventures.
          </p>
          <p className="font-body text-stone-500 text-sm md:text-base leading-relaxed">
            Whether you're launching your first venture or scaling an
            established brand, we are committed to supporting your journey every
            step of the way.
          </p>
        </div>
      </div>
    </section>
  );
}

function WhatWeHave() {
  const [ref, inView] = useInView(0.1);
  return (
    <section
      ref={ref}
      className="max-w-7xl mx-auto px-4 sm:px-8 md:px-12 lg:px-20 pb-20 md:pb-28"
    >
      <h2
        className={`font-display font-normal text-stone-900 leading-tight mb-10 md:mb-12 transition-all duration-700 ${inView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}
        style={{ fontSize: "clamp(1.8rem, 4vw, 3rem)" }}
      >
        What We Have For You?
      </h2>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
        {services.map((s, i) => (
          <div
            key={s.num}
            className={`relative overflow-hidden rounded-br-[40px] group cursor-pointer transition-all duration-700 ${inView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`}
            style={{ transitionDelay: `${i * 100}ms`, aspectRatio: "3/4" }}
          >
            <img
              src={s.img}
              alt={s.title}
              loading="lazy"
              className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/40 to-black/10" />
            <div className="absolute top-4 left-4">
              <span className="font-body text-white/60 text-[18px] tracking-[0.15em]">
                {s.num}
              </span>
            </div>
            <div className="absolute bottom-0 left-0 right-0 p-5">
              <p className="font-display text-white text-lg font-light mb-2 leading-snug">
                {s.title}
              </p>
              <p className="font-body text-white/55 text-xs leading-relaxed">
                {s.desc}
              </p>
            </div>
            <div className="absolute inset-0 rounded-br-[40px] border border-transparent group-hover:border-gold/50 transition-all duration-300" />
          </div>
        ))}
      </div>
    </section>
  );
}

export default function AboutPage() {
  return (
    <div className="min-h-screen bg-white pt-20">
      <Hero />
      <IntroCard />
      <WhoWeAre />
      <WhatWeHave />
    </div>
  );
}
